import React from 'react';
import { BaseInput, BaseInputProps } from './BaseInput';

export interface UseAsyncInputOptions {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    pattern?: RegExp;
    patternErrorMessage?: string;
    syncValidator?: (value: string) => string;
    asyncValidator?: (value: string, ...args: any[]) => Promise<string>;
    requiredMessage?: string;
    dependencies?: any[];
}

export interface UseAsyncInputReturn {
    value: string;
    error: string;
    isValidating: boolean;
    handleChange: (value: string) => void;
    handleBlur: (...args: any[]) => Promise<void>;
    validate: (...args: any[]) => Promise<string>;
    setError: (error: string) => void;
    setValue: (value: string) => void;
    isValid: boolean;
}

export const useAsyncInput = (options: UseAsyncInputOptions = {}): UseAsyncInputReturn => {
    const {
        required = false,
        minLength,
        maxLength,
        pattern,
        patternErrorMessage = 'Invalid format',
        syncValidator,
        asyncValidator,
        requiredMessage = 'This field is required',
        dependencies = []
    } = options;

    const [value, setValue] = React.useState<string>('');
    const [error, setError] = React.useState<string>('');
    const [isValidating, setIsValidating] = React.useState<boolean>(false);

    // Clear async validation errors when dependencies change
    const previousDependencies = React.useRef<any[]>(dependencies);
    React.useEffect(() => {
        const dependenciesChanged = previousDependencies.current.length !== dependencies.length ||
            previousDependencies.current.some((dep, index) => dep !== dependencies[index]);
        
        if (dependenciesChanged && error && asyncValidator) {
            setError('');
        }
        previousDependencies.current = dependencies;
    }, dependencies);

    const syncValidate = (inputValue?: string): string => {
        const currentValue = inputValue !== undefined ? inputValue : value;
        
        // Required validation
        if (required && !currentValue.trim()) {
            return requiredMessage;
        }
        
        // Skip other validations if not required and empty
        if (!required && !currentValue.trim()) {
            return '';
        }
        
        // Min length validation
        if (minLength && currentValue.trim().length < minLength) {
            return `Must be at least ${minLength} characters`;
        }
        
        // Max length validation
        if (maxLength && currentValue.trim().length > maxLength) {
            return `Cannot exceed ${maxLength} characters`;
        }
        
        // Pattern validation
        if (pattern && !pattern.test(currentValue.trim())) {
            return patternErrorMessage;
        }
        
        // Custom sync validation
        if (syncValidator) {
            const customError = syncValidator(currentValue);
            if (customError) {
                return customError;
            }
        }
        
        return '';
    };

    const handleChange = (newValue: string) => {
        setValue(newValue);
        // Clear error when user starts typing
        if (error && newValue) {
            setError('');
        }
    };

    const handleBlur = async (...args: any[]) => {
        // First do synchronous validation
        const syncError = syncValidate();
        if (syncError) {
            setError(syncError);
            return;
        }

        // If synchronous validation passes and we have async validator, do async validation
        if (asyncValidator && value.trim()) {
            setIsValidating(true);
            try {
                const asyncError = await asyncValidator(value, ...args);
                setError(asyncError);
            } catch (error) {
                console.error('Error during async validation:', error);
                setError('Failed to validate. Please try again.');
            } finally {
                setIsValidating(false);
            }
        }
    };

    const validate = async (...args: any[]): Promise<string> => {
        const syncError = syncValidate();
        if (syncError) {
            setError(syncError);
            return syncError;
        }

        if (asyncValidator && value.trim()) {
            setIsValidating(true);
            try {
                const asyncError = await asyncValidator(value, ...args);
                setError(asyncError);
                return asyncError;
            } catch (error) {
                console.error('Error during async validation:', error);
                const errorMsg = 'Failed to validate. Please try again.';
                setError(errorMsg);
                return errorMsg;
            } finally {
                setIsValidating(false);
            }
        }

        return '';
    };

    return {
        value,
        error,
        isValidating,
        handleChange,
        handleBlur,
        validate,
        setError,
        setValue,
        isValid: !error && (!required || !!value) && !isValidating
    };
};

export interface AsyncInputProps extends Omit<BaseInputProps, 'value' | 'onChange' | 'error'> {
    value: string;
    onChange: (value: string) => void;
    error?: string;
}

export const AsyncInput: React.FC<AsyncInputProps> = ({
    value,
    onChange,
    error,
    ...baseProps
}) => {
    return (
        <BaseInput
            {...baseProps}
            value={value}
            onChange={onChange}
            error={error}
        />
    );
};

// Generic interface for specific async input components
export interface BaseAsyncInputComponentProps extends AsyncInputProps {
    // Support for dynamic props
    [key: string]: any;
}

// Factory function to create async input components with less boilerplate
export const createAsyncInputComponent = (
    name: string,
    config: {
        type?: 'text' | 'textarea';
        rows?: number;
        maxLength?: number;
        getPlaceholder?: (props: any) => string;
        getAriaLabel?: (props: any) => string;
        getValidated?: (props: any) => 'error' | 'warning' | 'success' | 'default';
        defaultPlaceholder?: string;
    } = {}
) => {
    const AsyncInputComponent: React.FC<BaseAsyncInputComponentProps> = ({
        value,
        onChange,
        placeholder,
        ariaLabel,
        validated,
        ...props
    }) => {
        // Determine placeholder dynamically if getPlaceholder is provided
        const finalPlaceholder = placeholder || 
            (config.getPlaceholder ? config.getPlaceholder(props) : config.defaultPlaceholder);
        
        // Determine aria label dynamically if getAriaLabel is provided
        const finalAriaLabel = ariaLabel || 
            (config.getAriaLabel ? config.getAriaLabel(props) : name);

        // Determine validated state dynamically if getValidated is provided
        const finalValidated = validated || 
            (config.getValidated ? config.getValidated(props) : 'default');

        return (
            <AsyncInput
                {...props}
                value={value}
                onChange={onChange}
                placeholder={finalPlaceholder}
                ariaLabel={finalAriaLabel}
                validated={finalValidated}
                type={config.type}
                rows={config.rows}
                maxLength={config.maxLength}
            />
        );
    };

    AsyncInputComponent.displayName = name;
    return AsyncInputComponent;
};